/**
 * What the look cache is sized by: `LOOK_CACHE` says each.
 */
export interface LookCacheKnobs {
  readonly pages: number;
  readonly pageSize: number;
  readonly composesPerFrame: number;
  readonly fetches: number;
  readonly looks: number;
  readonly evictMargin: number;
  readonly retryMs: number;
  readonly retryMaxMs: number;
}

/**
 * The look cache's knobs (ADR-094 decision 15), all in one place.
 *
 * - `pages` and `pageSize`: how many look pages there may be and the side of each, a square render target. A page
 *   holds as many blocks as fit whole (24 of 512 by 320 on 2048), so five pages are 120 blocks: block 0, the fallback
 *   look's, and 119 looks, 16 MiB of texture a page and 80 MiB at the cap. A page is made when its first block is
 *   needed and kept from then on. Low graphics may lower `pages`: fewer distinct looks, never fewer characters.
 * - `composesPerFrame`: how many blocks are drawn a frame at most, nearest first.
 * - `fetches`: how many piece sheets are fetched at once.
 * - `looks`: how many looks the cache names at once, a block or not: the 512 a view holds and the 120 blocks, with
 *   room. A look named past it would be handed the fallback for good, since nothing names it again when another is
 *   let go; no page reaches it, a view's characters and the blocks' looks being 631 at most.
 * - `evictMargin`: how much nearer the page's own character, in world pixels, a look must be than the farthest look
 *   drawn this frame to take its block; one tile of 32 pixels, so two looks at about one distance do not swap blocks
 *   every frame.
 * - `retryMs` and `retryMaxMs`: how long the piece sheets that failed to load wait before they are asked for again,
 *   and the most that wait, which doubles with each round, grows to (5 s, then 10, 20, 40 and 80 from there on). Only
 *   a sheet a named look still needs is asked for again.
 *
 * The piece sheets have no knob: the cache holds every sheet the pointer names once a look has needed it (64 sheets
 * of 0.64 MiB for the first table) and lets none go; a cap with least recent use is for the row that grows the table
 * (decision 16).
 */
export const LOOK_CACHE: LookCacheKnobs = Object.freeze({
  pages: 5,
  pageSize: 2048,
  composesPerFrame: 2,
  fetches: 6,
  looks: 640,
  evictMargin: 32,
  retryMs: 5000,
  retryMaxMs: 80_000,
});

/**
 * How many blocks the look pages hold in all, the fallback's block 0 included.
 *
 * @param block A block's size in pixels, the layout's.
 * @param knobs The knobs; the game's unless given.
 * @returns The count: 120 for a block of 512 by 320.
 * @throws RangeError when not even the fallback's block and one more fit.
 * @example
 * lookBlockCount({ width: 512, height: 320 }); // 120: the fallback and 119 looks
 */
export function lookBlockCount(block: { readonly width: number; readonly height: number }, knobs: { readonly pages: number; readonly pageSize: number } = LOOK_CACHE): number {
  const count = knobs.pages * Math.floor(knobs.pageSize / block.width) * Math.floor(knobs.pageSize / block.height);
  if (!(count >= 2)) {
    throw new RangeError(`${knobs.pages} look pages of ${knobs.pageSize} hold ${count} blocks of ${block.width} by ${block.height}; the cache needs the fallback's and one more`);
  }
  return count;
}
